/**
 * The page around RevenueCat's checkout, in the app's look (its stylesheet, fonts and components): the wordmark and
 * the language picker atop, a card saying where things stand with a way back to the app, and at the foot the legal
 * documents and who handles the payment.
 */
import { Select } from "../../web/src/ui/form.tsx";
import { Logo } from "../../web/src/ui/Logo.tsx";
import { buttonClass, LINK } from "../../web/src/ui/styles.ts";
import revenuecat from "./brands/revenuecat.svg";
import stripe from "./brands/stripe.svg";
import type { View } from "./pay.ts";
import { LANGS, type Lang, privacyUrl, stringsOf, termsUrl } from "./strings.ts";

const WRAP = "mx-auto w-full max-w-xl px-4 sm:px-8";
const ERROR = "{error}";

/** Something on its way: the app's spinner (a ring in the text's color). */
function Spinner() {
  return (
    <span
      aria-hidden="true"
      class="inline-block size-5 animate-spin rounded-full border-2 border-current border-r-transparent text-blue-600"
    />
  );
}

/** A provider's logo, as it gives it (unaltered), with room around it. */
function Brand({ src, name }: { src: string; name: string }) {
  return <img src={src} alt={name} class="inline-block h-5 w-auto px-1" />;
}

export function Page(p: {
  view: View;
  /** The app's address (the wordmark goes there). */
  appUrl: string;
  /** Shows the page in another language (the address keeps it). */
  onLang: (lang: Lang) => void;
  /** Starts again (after a cancelled or failed checkout). */
  onAgain: () => void;
}) {
  const { view } = p;
  const s = stringsOf(view.lang);
  const said = s[view.state];
  const again = view.state === "cancelled" || view.state === "failed";
  return (
    <div class="flex min-h-dvh flex-col bg-paper text-zinc-900">
      <header class={`${WRAP} flex items-center justify-between gap-3 py-4 sm:py-6`}>
        <a
          href={p.appUrl}
          class="rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
        >
          <Logo class="text-3xl text-graphite" />
        </a>
        <div class="w-44">
          <Select
            compact={true}
            aria-label={s.language}
            value={view.lang}
            onValue={(v) => p.onLang(v as Lang)}
            options={LANGS.map((l) => ({ value: l.lang, label: l.name }))}
          />
        </div>
      </header>
      <main class={`${WRAP} flex flex-1 flex-col justify-center py-8`}>
        <section
          aria-live="polite"
          aria-busy={view.busy}
          class="rounded-lg border border-rule bg-sheet p-6 shadow-xs sm:p-8"
        >
          {view.busy ? <Spinner /> : null}
          <h1 class={`font-serif text-3xl leading-tight text-ink ${view.busy ? "mt-4" : ""}`}>{said.title}</h1>
          <p class="mt-3 text-zinc-600">{said.body}</p>
          {view.detail ? <p class="mt-2 text-sm text-zinc-500">{s.why.replace(ERROR, view.detail)}</p> : null}
          {view.busy ? null : (
            <div class="mt-6 flex flex-wrap gap-2">
              <a href={view.back} class={buttonClass("primary", "md")}>
                {s.back}
              </a>
              {again ? (
                <button type="button" class={buttonClass("secondary", "md")} onClick={p.onAgain}>
                  {s.again}
                </button>
              ) : null}
            </div>
          )}
        </section>
      </main>
      <footer class={`${WRAP} flex flex-col gap-3 pb-8 text-xs text-zinc-500`}>
        <p class="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span>{s.payments}</span>
          <Brand src={stripe} name="Stripe" />
          <span aria-hidden="true">·</span>
          <span>{s.subscriptions}</span>
          <Brand src={revenuecat} name="RevenueCat" />
        </p>
        <nav aria-label={s.legal} class="flex gap-4">
          <a href={termsUrl(view.lang)} class={LINK}>
            {s.terms}
          </a>
          <a href={privacyUrl(view.lang)} class={LINK}>
            {s.privacy}
          </a>
        </nav>
      </footer>
    </div>
  );
}
